import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import { Settings, MoreVertical, Pencil, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { generateTeamImageUrl, getTeamAvatarUrl, type Sport } from '@/utils/jersey-mapping';

export const Route = createFileRoute('/dashboard/sports/$id/teams/')({
  component: RouteComponent,
});

interface TeamData {
  externalId: string;
  leagueId: string;
  name: string;
  abbreviation?: string;
  logoUrl?: string;
  market?: string;
}

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Convert route id to lowercase for API (NFL -> nfl, NBA -> nba)
  const leagueId = id.toLowerCase();

  // TODO: This will come from DB when the functions have been sorted
  const { data: teams = [], isLoading } = useGetFeedTeamsQuery(leagueId);

  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim()) return teams;

    const query = searchQuery.toLowerCase();
    return teams.filter(
      team =>
        team.name.toLowerCase().includes(query) ||
        team.abbreviation?.toLowerCase().includes(query) ||
        team.externalId.toLowerCase().includes(query) ||
        team.market?.toLowerCase().includes(query)
    );
  }, [teams, searchQuery]);

  const handleEdit = (team: TeamData) => {
    navigate({
      to: '/dashboard/sports/$id/teams/$teamId',
      params: { id, teamId: team.externalId },
    });
  };

  const sportName = id.toUpperCase();

  const getNickname = (team: TeamData) => {
    const parts = team.name.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : team.name;
  };

  // Get full team name: "Market Name" (e.g., "Texas A&M Aggies")
  // Falls back to just name if market is not available
  const getFullTeamName = (team: TeamData) => {
    if (team.market && team.market !== team.name) {
      return `${team.market} ${team.name}`;
    }
    return team.name;
  };

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
        pathOverrides={{ sports: `/dashboard/sports/${id}` }}
      />

      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-500" />
          <h1 className="text-2xl font-semibold">
            {sportName} {'>'} Teams
          </h1>
        </div>

        <div className="flex-1 flex gap-2 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* TODO: Add Team button hidden until backend endpoint is available */}
      </div>

      <ContentCard>
        <Table>
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow>
              <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold">
                ID
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Logo</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Name</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Short Code</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Nick Name</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">League</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Avatar</TableHead>
              <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-8 rounded" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-8 rounded-full" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-8 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredTeams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="h-12 w-12 text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {searchQuery ? 'No teams found matching your search' : 'No teams found'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredTeams.map((team: TeamData) => (
                <TableRow key={team.externalId}>
                  <TableCell className="font-medium">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block max-w-[60px] truncate">{team.externalId}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{team.externalId}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const logoSrc =
                        team.logoUrl ||
                        (team.abbreviation
                          ? generateTeamImageUrl(team.abbreviation, sportName as Sport)
                          : null);
                      return logoSrc ? (
                        <img
                          src={logoSrc}
                          alt={team.name}
                          className="h-8 w-8 object-contain"
                          onError={e => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <Users className="h-4 w-4 text-gray-400" />
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="font-medium">{getFullTeamName(team)}</TableCell>
                  <TableCell>
                    {team.abbreviation ? (
                      <Badge variant="outline" className="font-mono">
                        {team.abbreviation}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getNickname(team)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{sportName}</Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const jerseyUrl = team.abbreviation
                        ? getTeamAvatarUrl(team.abbreviation, sportName as Sport)
                        : null;
                      return jerseyUrl ? (
                        <img
                          src={jerseyUrl}
                          alt={team.name}
                          className="h-8 w-8 rounded object-cover"
                          onError={e => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-teal-500 flex items-center justify-center">
                          <Users className="h-4 w-4 text-white" />
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(team)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ContentCard>
    </PageContainer>
  );
}
